import {Tag, Tooltip, Button, Alert, Typography} from 'antd';
import {useNavigate} from 'react-router-dom';
import moment from 'moment';
import {
  ACCOUNT_STATUS_COLORS,
  KYB_ACTION_COPY,
  KYB_STATUS_COLORS,
} from '../kybConstants';
import {
  activationBlocker,
  availableTransitions,
  canSubmitOnBehalf,
  describeMissingRequirement,
  formatEnumLabel,
  personName,
} from '../kybUtils';
import styles from '../kybDashboard.module.scss';

const {Text} = Typography;

/**
 * Identity, the two status indicators, and whatever decisions the current
 * status actually allows.
 *
 * Verification and activation are shown as separate indicators on purpose:
 * approving KYB does not make an account live, and a reviewer looking at
 * "Approved" next to "Pending verification" needs to see that the remaining
 * leg is the contact's own KYC — which lives in the KYC tooling, not here.
 *
 * `onSubmitForReview` is passed only to reviewers who may update KYB; it adds
 * a submit-on-behalf action while the account waits on the business.
 * `missingRequirements` is what a refused submission said was outstanding.
 */
export const KybAccountHeader = ({
  account,
  onDecision,
  onSubmitForReview,
  missingRequirements,
  onDismissMissing,
}) => {
  const navigate = useNavigate();
  const transitions = availableTransitions(account?.kybStatus);
  const blocker = activationBlocker(account);

  return (
    <div className={styles.detailHeader}>
      <div className={styles.detailHeaderTop}>
        <div>
          <div className={styles.entityTitleRow}>
            <h1 className={styles.entityTitle}>
              {account?.entityName || 'Unnamed business'}
            </h1>
            {account?.duplicateRegistrationFlag && (
              <Tooltip title="Another account uses this registration number. It is a flag for a human, not a block — approving is still your call.">
                <Tag color="gold">Duplicate registration</Tag>
              </Tooltip>
            )}
          </div>
          <Text type="secondary">
            {[
              account?.registrationNumber,
              account?.countryOfIncorporation,
              account?.businessEmail,
              account?.businessPhone,
            ]
              .filter(Boolean)
              .join('  ·  ') || 'No business details on file'}
          </Text>
        </div>

        <div className={styles.headerActions}>
          {transitions.map(status => (
            <Button
              key={status}
              type={status === 'APPROVED' ? 'primary' : 'default'}
              danger={status === 'REJECTED'}
              onClick={() => onDecision(status)}>
              {KYB_ACTION_COPY[status]?.label || formatEnumLabel(status)}
            </Button>
          ))}
          {!transitions.length && (
            <Text type="secondary" className={styles.noActions}>
              {account?.kybStatus === 'APPROVED'
                ? 'Approved — no further action.'
                : 'Waiting on the business to submit.'}
            </Text>
          )}
          {onSubmitForReview && canSubmitOnBehalf(account?.kybStatus) && (
            <Button onClick={onSubmitForReview}>
              Submit for review on the business’s behalf
            </Button>
          )}
        </div>
      </div>

      <div className={styles.indicatorRow}>
        <div className={styles.indicator}>
          <span className={styles.indicatorLabel}>Verification</span>
          <Tag color={KYB_STATUS_COLORS[account?.kybStatus] || 'default'}>
            {formatEnumLabel(account?.kybStatus)}
          </Tag>
        </div>

        <div className={styles.indicator}>
          <span className={styles.indicatorLabel}>Account</span>
          {account?.isFullyActivated ? (
            <Tag color="green">Live</Tag>
          ) : (
            <Tag color={ACCOUNT_STATUS_COLORS[account?.status] || 'default'}>
              {formatEnumLabel(account?.status)}
            </Tag>
          )}
        </div>

        <div className={styles.indicator}>
          <span className={styles.indicatorLabel}>Submitted</span>
          <span>
            {account?.kybSubmittedAt
              ? moment(account.kybSubmittedAt).format('DD MMM YYYY, HH:mm')
              : '—'}
          </span>
        </div>

        <div className={styles.indicator}>
          <span className={styles.indicatorLabel}>Reviewed</span>
          <span>
            {account?.kybReviewedAt
              ? `${moment(account.kybReviewedAt).format('DD MMM YYYY, HH:mm')}${
                  account?.kybReviewer
                    ? ` by ${personName(account.kybReviewer)}`
                    : ''
                }`
              : '—'}
          </span>
        </div>

        {account?.activatedAt && (
          <div className={styles.indicator}>
            <span className={styles.indicatorLabel}>Live since</span>
            <span>{moment(account.activatedAt).format('DD MMM YYYY')}</span>
          </div>
        )}
      </div>

      {missingRequirements?.length > 0 && (
        <Alert
          type="warning"
          showIcon
          closable={Boolean(onDismissMissing)}
          onClose={onDismissMissing}
          className={styles.alert}
          message="Not submitted. The checklist still needs:"
          description={
            <ul className={styles.missingList}>
              {missingRequirements.map((requirement, index) => (
                <li key={`${requirement.key || 'row'}-${index}`}>
                  {describeMissingRequirement(requirement)}
                </li>
              ))}
            </ul>
          }
        />
      )}

      {blocker?.kind === 'CONTACT_KYC' && (
        <Alert
          type="info"
          showIcon
          className={styles.alert}
          message={blocker.message}
          action={
            account?.primaryContact?.id ? (
              <Button
                size="small"
                onClick={() =>
                  navigate(`/dashboard/user/${account.primaryContact.id}`)
                }>
                Open contact
              </Button>
            ) : null
          }
        />
      )}

      {account?.kybReviewReason && (
        <Alert
          type="warning"
          showIcon
          className={styles.alert}
          message={`Last reason sent to the business: ${account.kybReviewReason}`}
        />
      )}
    </div>
  );
};
